const commerceSdkPromise = import('commerce-sdk-isomorphic')

// Module-level cache - one set of SDK clients per locale, shared across all SSR requests (server)
// or reused within the page lifecycle (client). Safe because:
//   - SDK client config (clientId, orgId, shortCode, siteId, proxy) is identical for every request
//     to the same server process - all values come from static env vars or locale config.
//   - Auth tokens are NOT stored on the clients; they are passed per-call via the `headers` parameter.
//   - Server and client are separate Vite bundles, so server module state never leaks to the browser.
// Without this, every SSR request allocates 9 new SDK client instances (each with its own HTTP agent
// and connection pool), causing rapid heap growth under concurrent traffic.
let moduleCommerceApi: {
  getClientsForLocale: (locale: Locales) => any
  helpers: any
  sdk: any
} | null = null
const sdkClientCache = new Map<Locales, Record<string, any>>()

/* Parallel execution causes potential race condition as shopper-context.client.ts requires $commerce before its initialized, removing parallel enforces this plugin to run first based on order of execution https://nuxt.com/docs/4.x/directory-structure/app/plugins#loading-strategy */
export default defineNuxtPlugin({
  name: 'commerce',
  // Must be false (sequential) so $commerce is guaranteed to be registered before any composable or pinia setup calls useShopper() referencing nuxtApp.$commerce.
  // When parallel true, stores instantiated by SSR-rendered components (e.g. useWishlistStore in Header) could run before this plugin resolves, resulting in "$commerce is not available" errors.
  parallel: false,

  async setup() {
    if (moduleCommerceApi) {
      return { provide: { commerce: moduleCommerceApi } }
    }

    // In production builds the dynamic import may resolve as a CJS-wrapped module with the actual exports on `.default`.
    // Fall back to the raw module if `.default` doesn't have the expected constructor (e.g. in Vite dev the named exports are top-level).
    const commerceSdkRaw = await commerceSdkPromise
    const commerceSdk =
      'ShopperLogin' in commerceSdkRaw ? commerceSdkRaw : ((commerceSdkRaw as any).default ?? commerceSdkRaw)
    const runtimeConfig = useRuntimeConfig()
    const requestOrigin = resolveRequestOrigin()

    function buildClientBundle(locale: Locales) {
      const localeEntry = localeConfig[locale]
      if (!localeEntry) {
        throw new Error(`[commerce] No localeConfig entry found for locale '${locale}'`)
      }
      const siteId = localeEntry.siteId

      if (!siteId) {
        throw new Error(
          `[commerce] Missing SFCC siteId configuration for locale '${locale}', requestOrigin '${requestOrigin}'`,
        )
      }

      const clientConfig = {
        // Client-side: route SDK calls through /proxy to avoid CORS (browser -> same origin -> SFCC).
        // Server-side: call SFCC directly - no CORS restriction for server-to-server,
        // and routing through /proxy would cause recursive requests back into the same Nuxt server (potential OOM).
        proxy: import.meta.server ? undefined : `${requestOrigin}/proxy`,
        headers: {},
        parameters: {
          clientId: runtimeConfig.public.SFCC_CLIENT_ID,
          organizationId: runtimeConfig.public.SFCC_ORGANIZATION_ID,
          shortCode: runtimeConfig.public.SFCC_SHORT_CODE,
          siteId,
        },
        throwOnBadResponse: true,
      }

      return {
        config: clientConfig.parameters,
        shopperLogin: new commerceSdk.ShopperLogin(clientConfig),
        shopperCustomers: new commerceSdk.ShopperCustomers(clientConfig),
        shopperSearch: new commerceSdk.ShopperSearch(clientConfig),
        shopperProducts: new commerceSdk.ShopperProducts(clientConfig),
        shopperBaskets: new commerceSdk.ShopperBaskets(clientConfig),
        shopperOrder: new commerceSdk.ShopperOrders(clientConfig),
        shopperPromotions: new commerceSdk.ShopperPromotions(clientConfig),
        shopperStores: new commerceSdk.ShopperStores(clientConfig),
        shopperContexts: new commerceSdk.ShopperContexts(clientConfig),
      }
    }

    moduleCommerceApi = {
      getClientsForLocale(locale: Locales) {
        // Fall back to 'en-AU' if locale is unresolvable (e.g. internal health check, hitting the
        // server via GCP pod name with no Host header i18n can match to a configured domain).
        const resolvedLocale: Locales = localeConfig[locale] ? locale : 'en-AU'
        if (!sdkClientCache.has(resolvedLocale)) {
          sdkClientCache.set(resolvedLocale, buildClientBundle(resolvedLocale))
        }
        return sdkClientCache.get(resolvedLocale)!
      },
      helpers: commerceSdk.helpers,
      sdk: commerceSdk,
    }

    return {
      provide: {
        commerce: moduleCommerceApi,
      },
    }
  },
})
